// Package Imports
import { useOne, useTranslate, useUpdate } from '@refinedev/core';
import { useWatch } from 'antd/es/form/Form';
import { useForm } from '@refinedev/antd';
import { useEffect, useState } from 'react';
// Project Imports
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import constants from 'core/constants/Constants';
import openNotificationWithIcon from 'core/utils/notification';
// prettier-ignore
import { getDateRange, getDmnXmlString, getUnifiedList, onSelectRuleGroupfn, ruleGroupApiResponse, } from 'pages/rules/ruleApiResponse';
import RuleMainView from 'pages/rules/ruleMainView';
import updatedValues from 'pages/rules/utils/updatedValues';
import changedKeysFn from 'pages/rules/utils/changedValues';
import Loading from 'components/loading/loading';
// prettier-ignore
import { RulesCreateStateProvider, useRulesCreateState, } from 'pages/rules/context/RulesCreateStateContext';
import getQueryBuilderError from 'pages/rules/utils/getQueryBuilderError';
import saveRule from 'pages/rules/utils/saveRule';
// prettier-ignore
import { transformInitialRuleData, transformNewVersionRuleData, } from 'pages/rules/utils/stateTransformUtils';
import parseDmn from 'components/modules/dmn/src/utils/parseDmn';
import validateDmnAndQueryBuilder from 'pages/rules/utils/validateDmnAndQueryBuilder';
import dayjs from 'dayjs';

// prettier-ignore
const RulesCreateInner: React.FC<{ type: string; id?: string }> = ({type,id}) => {
  const t = useTranslate();

  const { refetchRuleDetails, setRefetchRuleDetails, rulePlanDataObj } =
    useRulesContext();
  const { mutate } = useUpdate();
  const state = useRulesCreateState();
  const [saveLoading, setSaveLoading] = useState(false);
  const { onFinish, formProps, saveButtonProps, form } = useForm({
    resource: `${type === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.rules : apiEndpoint.rulesDraft}`,
    action: 'create',
    successNotification: false,
    errorNotification: false,
    redirect: false,
    queryOptions: { enabled: false },
    onMutationSuccess: (data) => {
      const message = data?.data?.message || t('notifications.createSuccess');
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        message
      );
      setRefetchRuleDetails(!refetchRuleDetails);
      setSaveLoading(false);
    },
    onMutationError(error) {
      const message = error?.message || t('notifications.createError');
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        message
      );
      setSaveLoading(false);
    },
  });

  const newruleVersion = useWatch('rule_version', { form, preserve: true });
  // prettier-ignore
  const ruleApplicabilty = useWatch('rule_expression', { form, preserve: true });
  // prettier-ignore
  const ruleApplicabilityModeType = useWatch('rule_applicability_mode_type', { form, preserve: true });
  const outputformula = useWatch('output_formula', { preserve: true, form });
  // prettier-ignore
  const outputFormulaModeType = useWatch('output_formula_mode_type', { form, preserve: true });
  const clawbackTypeVal = useWatch('clawback_type', { form, preserve: true });

  useEffect(() => {
    if (clawbackTypeVal === 'PARTIAL') {
      setTimeout(() => {
        form.setFieldsValue({
          output_formula_type: 'simple',
          output_formula_editor_type: 'wizard',
          output_formula_mode_type: 'single',
          rule_file_type: 'dmn',
          file_upload_type: 'generate',
        });
      }, 100);
    }
  }, [clawbackTypeVal]);
  const { ruleGroupList, isFetching: ruleGroupFetching } =
    ruleGroupApiResponse(type);
  const { data: ruleGroupData, isFetching } = useOne({
    resource: `${type === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.rules : apiEndpoint.rulesDraft}`,
    id: state.editId,
    queryOptions: { enabled: state.allowEdit },
  });

  const ruleData = ruleGroupData?.data?.results;
  const uniqueId = useWatch('unique_id', { form, preserve: true });
  const currentRuleFileType = state?.ruleData?.filter(
    (item) => item?.rule_version === newruleVersion
  )[0]?.rule_file_type;

  const { dmnXmlList } = getDmnXmlString(uniqueId, currentRuleFileType, type);
  useEffect(() => {
    if (dmnXmlList?.xml_string && uniqueId) {
      state.setXmlStructure(dmnXmlList.xml_string);
      state.setPreviousXmlValues(dmnXmlList.xml_string);
    }
  }, [dmnXmlList?.xml_string, uniqueId]);

  useEffect(() => {
    if (ruleData?.length > 0) {
      const updatedRuleData = ruleData.map((item) => {
        if (
          item?.rule_applicability?.query === '*' &&
          item?.output_formula?.query === '*'
        ) {
          return {
            ...item,
            rule_group_configuration_status: false,
            enter_amount: item?.output_formula?.value,
          };
        }
        return item;
      });

      const transformedState = transformInitialRuleData(updatedRuleData);
      if (transformedState) {
        form.setFieldsValue(transformedState.formValues);
        state.setInputSourceFileData(transformedState.inputSourceFileData);
        state.setPreviousValues(transformedState.previousValues);
      }
      state.setRuleData(updatedRuleData);
    }
  }, [ruleData]);
  useEffect(() => {
    if (newruleVersion) {
      const transformedState = transformNewVersionRuleData(
        ruleData,
        newruleVersion
      );
      if (transformedState) {
        form.setFieldsValue(transformedState.formValues);
        state.setInputSourceFileData(transformedState.inputSourceFileData);
        state.setPreviousValues(transformedState.previousValues);
      }
    }
  }, [newruleVersion]);
  const ruleGroupId = useWatch('rule_group', { form, preserve: true });
  const ruleType =
    ruleGroupList?.length > 0
      ? ruleGroupList?.filter((item) => item?.unique_id === ruleGroupId)[0]
        ?.rule_type
      : '';
  const onFinishHandler = async (value: any) => {
    setSaveLoading(true);
    state.setIsSavedClick(true);
    const clawbackTypeValOnSave = form.getFieldValue('clawback_type');
    const isClawbackDirect =
      ruleType === 'clawback' &&
      (clawbackTypeValOnSave === 'RECALCULATION' ||
        clawbackTypeValOnSave === 'FULL');
    const isClawback =
      ruleType === 'clawback' && clawbackTypeValOnSave !== 'PARTIAL';
    if (!isClawbackDirect && !isClawback) {
      const validationResult = validateDmnAndQueryBuilder({
        ruleApplicabilty,
        ruleApplicabilityModeType,
        outputformula,
        outputFormulaModeType,
        state,
        value,
      });
      if (!validationResult.isValid) {
        setSaveLoading(false);
        return;
      }
    }
    const isLatest = (state.previousValues as any)?.is_latest;
    const updatedValue = updatedValues(
      value,
      form.getFieldValue('output_formula'),
      form.getFieldValue('rule_expression'),
      state.configFields,
      state.inputSourceFileData
    );
    const currentValue = { ...value, ...updatedValue };
    const previousDmnData = parseDmn(state.previousXmlValues);
    const changedKeys = changedKeysFn(
      currentValue,
      state.previousValues,
      state.dmnData,
      previousDmnData
    );
    const ruleValue = {
      ...currentValue,
      // rule_visibility: userDetails?.user_employee_id,
      // rule_type: 'COMMON',
      is_new_dmn_uploaded: isClawbackDirect ? false : changedKeys?.dmnChanged,
    };
    state.setCurrentValues(ruleValue);
    if (
      (changedKeys?.valuesChanged || changedKeys?.dmnChanged) &&
      state.pageType !== 'Create' &&
      isLatest
    ) {
      state.setNewVersionModal(true);
      setSaveLoading(false);
    } else {
      await saveRule(
        ruleValue,
        'false',
        state,
        mutate,
        uniqueId,
        form,
        setRefetchRuleDetails,
        refetchRuleDetails,
        onFinish,
        t,
        rulePlanDataObj,
        setSaveLoading,
      );
    }
  };
  const onEdit = (val: string) => {
    state.setAllowEdit(true);
    state.setEditId(val);
  };
  useEffect(() => {
    if (type === 'Edit' || type === 'Show') {
      onEdit(id);
      state.setPageType(type === 'Edit' ? 'Show' : type); // can put directly Show
    }
  }, [type]);
  const unifiedListUnfiltered = getUnifiedList({ filterType: 'unfiltered' });
  const ruleGroupDate = ruleGroupId
    ? getDateRange(ruleGroupList, ruleGroupId)
    : [];
  function onSelectRuleGroup(e) {
    onSelectRuleGroupfn(e, ruleGroupList, form, state.allowEdit, ruleData);
  }
  useEffect(() => {
    if (ruleGroupList?.length > 0) {
      state.setRuleGroupList(ruleGroupList);
      if (ruleGroupList?.length === 1) {
        const value = ruleGroupList?.[0]?.unique_id;
        form.setFieldValue('rule_group', value);
        onSelectRuleGroup(value);
      }
    }
  }, [ruleGroupList]);
  useEffect(() => {
    if (ruleGroupList?.length > 1 && ruleGroupId && type === 'Create') {
      const selectedRuleGroupObj = ruleGroupList?.filter(
        (item) => item?.unique_id === ruleGroupId
      )[0];
      const startDate = dayjs(selectedRuleGroupObj?.rule_group_start_date);
      const endDate = dayjs(selectedRuleGroupObj?.rule_group_end_date);
      form.setFieldsValue({
        rules_date: [startDate, endDate],
      });
    }
  }, [ruleGroupId]);
  useEffect(() => {
    state.setRuleApplicabilityError(
      getQueryBuilderError(ruleApplicabilty, ruleApplicabilityModeType)
    );
    if (constants.payoutTypes.includes(outputformula?.type)) {
      state.setOutputFormulaError(
        getQueryBuilderError(outputformula, outputFormulaModeType)
      );
    }
  }, [ruleApplicabilty, outputformula]);
  if (isFetching || ruleGroupFetching) {
    return <Loading />;
  }

  return (
    <RuleMainView
      form={form}
      onFinishHandler={onFinishHandler}
      saveButtonProps={saveButtonProps}
      formProps={formProps}
      unifiedListUnfiltered={unifiedListUnfiltered}
      ruleGroupDate={ruleGroupDate}
      ruleType={ruleType}
      type={type}
      onFinish={onFinish}
      saveLoading={saveLoading}
      setSaveLoading={setSaveLoading}
    />
  );
};
const RulesCreate = (props) => (
  <RulesCreateStateProvider>
    <RulesCreateInner {...props} />
  </RulesCreateStateProvider>
);

export default RulesCreate;
